import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import AdminShell from "@/components/AdminShell";
import { EqualMark, Eyebrow, StepNumber } from "@/components/brand";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CityDashboard() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWelcome, setShowWelcome] = useState(false);
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/city/notifications`, { headers: { Authorization: `Bearer ${token}` } });
      setNotifications(res.data.notifications || []);
    } catch { toast.error("Failed to load notifications"); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => {
    if (!localStorage.getItem("equal_city_welcomed")) {
      setShowWelcome(true);
    }
    fetchNotifications();
  }, [fetchNotifications]);

  const cards = [
    {
      id: "conversations", path: "/admin/conversations", label: "All Conversations",
      sub: "Read every applicant session and search transcripts", testId: "view-conversations-card",
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/></svg>
    },
    {
      id: "report", path: "/admin/report", label: "Monthly Report",
      sub: "Program snapshot and recurring subjects across months", testId: "view-report-card",
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
    },
    {
      id: "analytics", path: "/admin/analytics", label: "Analytics",
      sub: "FAQ trends, knowledge gaps, and session engagement data", testId: "view-analytics-card",
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/></svg>
    },
    {
      id: "invites", path: "/admin/invites", label: "Invitations",
      sub: "Invite staff and applicants, track who accepted", testId: "view-invites-card",
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
    },
    {
      id: "comps", path: "/comps", label: "Market Data",
      sub: "Lease comps, benchmarks and rent trends", testId: "view-comps-card",
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 13a4 4 0 014-4h10a4 4 0 014 4M3 13v6a2 2 0 002 2h14a2 2 0 002-2v-6M3 13l2-7a2 2 0 012-1.5h10A2 2 0 0119 6l2 7"/></svg>
    },
    {
      id: "studio", path: "/studio", label: "Design Studio",
      sub: "Applicants' floor plans, LBMC 5.92 checks and budgets", testId: "view-studio-card",
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v14a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm0 6h16M10 4v16"/></svg>
    },
  ];

  const dismissWelcome = () => { setShowWelcome(false); localStorage.setItem("equal_city_welcomed", "1"); };

  return (
    <AdminShell active="dashboard" logoutTestId="city-logout-button">
      <div className="mb-12">
        <Eyebrow className="mb-3">City of Long Beach</Eyebrow>
        <h1 className="text-4xl sm:text-5xl font-extrabold text-ink tracking-tight leading-[1.05]">
          Official Portal
        </h1>
        <p className="text-mute mt-4 text-base max-w-xl">Read-only access to applicant sessions and program metrics.</p>
      </div>

      {/* Nav cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mb-12">
        {cards.map((card, i) => (
          <button key={card.id} onClick={() => navigate(card.path)}
            className="card-flat p-5 text-left group hover:border-[#b9c4d0] hover:-translate-y-px transition-[border-color,transform] duration-200 animate-rise-in"
            style={{ animationDelay: `${i * 40}ms` }}
            data-testid={card.testId}>
            <div className="flex items-start justify-between">
              <div className="w-9 h-9 rounded-[10px] bg-mint text-equal-dark flex items-center justify-center mb-5">
                {card.icon}
              </div>
              <span className="font-mono text-[11px] text-mute/70">{String(i + 1).padStart(2, "0")}</span>
            </div>
            <p className="text-ink font-semibold text-[14.5px] mb-1 tracking-tight">{card.label}</p>
            <p className="text-mute text-xs leading-relaxed">{card.sub}</p>
            <div className="mt-4 flex items-center gap-1 eyebrow group-hover:text-equal-dark transition-colors">
              <span>Open</span>
              <svg className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/>
              </svg>
            </div>
          </button>
        ))}
      </div>

      {/* Session reports */}
      <div>
        <Eyebrow className="mb-4">Session Reports</Eyebrow>
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-line border-t-equal rounded-full animate-spin" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="card-flat text-center py-12" data-testid="no-notifications-message">
            <p className="text-ink text-sm font-medium">No session reports yet.</p>
            <p className="text-mute text-xs mt-1">Reports appear automatically when applicants end their sessions.</p>
          </div>
        ) : (
          <div className="card-flat overflow-hidden">
            <ScrollArea className="h-96">
              <div className="divide-y divide-line">
                {notifications.map(notif => (
                  <div key={notif.id} className="flex items-center justify-between px-5 py-4 hover:bg-page transition-colors"
                    data-testid={`notification-${notif.id}`}>
                    <div>
                      <p className="text-ink text-sm font-medium">{notif.user_email}</p>
                      <p className="text-mute text-xs mt-0.5 font-mono">
                        {new Date(notif.session_start).toLocaleString()} — {notif.message_count} messages
                      </p>
                    </div>
                    <button onClick={() => navigate("/admin/conversations")}
                      className="btn-ghost btn-sm"
                      data-testid={`view-transcript-${notif.id}`}>
                      View
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/>
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}
      </div>

      {/* First-time city official welcome modal */}
      {showWelcome && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6 bg-ink/70">
          <div className="w-full max-w-lg rounded-2xl overflow-hidden bg-white border border-line max-h-[90vh] flex flex-col animate-rise-in">
            {/* Header */}
            <div className="px-8 py-7 flex-shrink-0 bg-ink text-white">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3 mb-3">
                  <EqualMark size={28} color="#fff" />
                  <span className="font-bold text-xl tracking-tight">Welcome to Equal</span>
                </div>
                <button onClick={dismissWelcome}
                  className="text-white/60 hover:text-white transition-colors flex-shrink-0 mt-1">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              </div>
              <p className="text-white/80 text-sm leading-relaxed">
                Read-only oversight of applicant sessions, transcripts and program analytics.
              </p>
            </div>

            {/* Scrollable content */}
            <div className="px-8 py-6 space-y-4 overflow-y-auto flex-1 bg-page">
              <Eyebrow>As a City Official, you can</Eyebrow>
              <div className="space-y-2">
                {[
                  { title: "View All Applicant Sessions", body: "Every conversation, logged in real time." },
                  { title: "Receive Session Transcripts", body: "A PDF is emailed when an applicant ends a session." },
                  { title: "Track Program Analytics", body: "Top questions, knowledge gaps, usage over time." },
                  { title: "Read-Only Access", body: "Nothing can be modified from this account." },
                ].map((item, i) => (
                  <div key={item.title} className="flex gap-3 p-3 rounded-[10px] bg-white border border-line">
                    <span className="mt-0.5"><StepNumber n={i + 1} active={i === 0} /></span>
                    <div>
                      <p className="text-sm font-semibold text-ink">{item.title}</p>
                      <p className="text-xs text-mute mt-0.5 leading-relaxed">{item.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Sticky footer button */}
            <div className="px-8 py-6 flex-shrink-0 border-t border-line">
              <button onClick={dismissWelcome} className="btn-primary w-full h-11">
                Go to Portal
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminShell>
  );
}
